import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Login } from './pages/Login';
import { AuthCallback } from './pages/AuthCallback';
import { Inicio } from './pages/Inicio';
import { InicioGlobal } from './pages/InicioGlobal';
import { AnalisisCliente } from './pages/analisis/AnalisisCliente';
import { RequireAuth } from './lib/RequireAuth';
import { AuthProvider } from './lib/AuthProvider';
import { ClienteActivoProvider } from './lib/cliente-activo';
import { TituloRegistroProvider } from './lib/page-title';
import { Layout } from './components/Layout';

import { ProductoDetalle } from './pages/productos/ProductoDetalle';
import { ProductoFicha } from './pages/productos/ProductoFicha';
import { ProductoNuevo } from './pages/productos/ProductoNuevo';
import { PedidoClienteDetalle } from './pages/pedidos-cliente/PedidoClienteDetalle';
import { ProveedoresList } from './pages/proveedores/ProveedoresList';
import { ProveedorDetalle } from './pages/proveedores/ProveedorDetalle';
import { ProveedoresIndex } from './pages/proveedores/ProveedoresIndex';
import { ComisionesPanel } from './pages/comisiones/ComisionesPanel';
import { PedidosProveedor } from './pages/proveedores/PedidosProveedor';
import { ImportadorProductos } from './pages/proveedores/ImportadorProductos';
import { CatalogoList } from './pages/catalogo/CatalogoList';
import { ProductoProveedorDetalle } from './pages/catalogo/ProductoProveedorDetalle';
import { ProductoProveedorNuevo } from './pages/catalogo/ProductoProveedorNuevo';
import { NuevoProductoDetalle } from './pages/nuevos-productos/NuevoProductoDetalle';
import { StockImport } from './pages/stock-import/StockImport';
import { CotizacionDetalle } from './pages/game/CotizacionDetalle';

import {
  GameProductos,
  GameHistoricoVentas,
  GameNuevosProductos,
  GamePedidos,
  GameCotizaciones,
  GameCalculadora,
  GameParametros,
  GameBolsaProvisionamiento,
} from './pages/game/GamePages';
import {
  TicnovaProductos,
  TicnovaNuevosProductos,
  TicnovaCalculadora,
  TicnovaCotizaciones,
  TicnovaParametros,
  TicnovaPedidos,
} from './pages/ticnova/TicnovaPages';

import {
  ClientesAdmin,
  MarcasAdmin,
  CategoriasAdmin,
  SubcategoriasAdmin,
  DetallesAdmin,
  ProductoEstadosAdmin,
  PedidoEstadosAdmin,
  ParametrosAdmin,
  EnviosAdmin,
  CondicionesComercialesAdmin,
  GameCalendarioFiscalAdmin,
  GamasAdmin,
  EmailTemplatesAdmin,
  TicnovaFamiliasMapAdmin,
  ProductoCodigosAliasAdmin,
  ProveedorAliasAdmin,
} from './pages/admin/AdminPages';

const ReposicionesList = lazy(() =>
  import('./pages/reposiciones/ReposicionesList').then((m) => ({ default: m.ReposicionesList })),
);
const ReposicionNueva = lazy(() =>
  import('./pages/reposiciones/ReposicionNueva').then((m) => ({ default: m.ReposicionNueva })),
);
const ReposicionDetalle = lazy(() =>
  import('./pages/reposiciones/ReposicionDetalle').then((m) => ({ default: m.ReposicionDetalle })),
);
const Importaciones = lazy(() =>
  import('./pages/game/Importaciones').then((m) => ({ default: m.Importaciones })),
);
const ImportadorFicheros = lazy(() =>
  import('./pages/importador/ImportadorFicheros').then((m) => ({ default: m.ImportadorFicheros })),
);

// Lazy: el uploader de Icecat solo carga al visitar /ticnova/icecat.
const TicnovaIcecat = lazy(() => import('./pages/ticnova/TicnovaIcecat'));

// Redirect helper que preserva el :id de la URL.
function RedirectWithId({ to }: { to: (id: string) => string }) {
  const { id = '' } = useParams<{ id: string }>();
  return <Navigate to={to(id)} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <ClienteActivoProvider>
      <TituloRegistroProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        <Route
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          {/* Centro de mando global: la pantalla de inicio (fase 2 del dashboard).
              El inicio por cliente sigue vivo en /game y /ticnova. */}
          <Route path="/" element={<InicioGlobal />} />

          {/* ───────── Comisiones (global, filtra por cliente dentro) ───────── */}
          <Route path="/comisiones" element={<ComisionesPanel />} />

          {/* ───────── Proveedores ───────── */}
          <Route path="/proveedores" element={<ProveedoresIndex />} />
          <Route path="/proveedores/listado" element={<ProveedoresList />} />
          <Route path="/proveedores/listado/:id" element={<ProveedorDetalle />} />
          <Route path="/proveedores/productos" element={<CatalogoList />} />
          <Route path="/proveedores/productos/nuevo" element={<ProductoProveedorNuevo />} />
          <Route path="/proveedores/productos/:id" element={<ProductoProveedorDetalle />} />
          <Route path="/proveedores/pedidos" element={<PedidosProveedor />} />
          <Route path="/proveedores/importador" element={<ImportadorProductos />} />
          {/* Aliases legacy */}
          <Route path="/proveedores-legacy" element={<Navigate to="/proveedores/listado" replace />} />
          <Route path="/catalogo-legacy" element={<Navigate to="/proveedores/productos" replace />} />

          {/* ───────── GAME ───────── */}
          <Route path="/game" element={<Inicio />} />
          <Route path="/game/analisis" element={<AnalisisCliente />} />
          <Route path="/game/productos" element={<GameProductos />} />
          <Route path="/game/historico-ventas" element={<GameHistoricoVentas />} />
          <Route path="/game/productos/nuevo" element={<ProductoNuevo clienteCodigo="GAME" />} />
          <Route path="/game/productos/:id" element={<ProductoFicha />} />
          <Route path="/game/productos/:id/clasica" element={<ProductoDetalle />} />
          <Route path="/game/nuevos-productos" element={<GameNuevosProductos />} />
          <Route path="/game/nuevos-productos/:id" element={<NuevoProductoDetalle />} />
          {/* Importador de ficheros (CEO, 26/10/2026): un solo sitio con un
              cuadro por importador; las rutas antiguas abren su pestaña. */}
          <Route
            path="/game/importador-ficheros"
            element={
              <Suspense fallback={<div className="p-4 text-sm text-slate-500">Cargando importador…</div>}>
                <ImportadorFicheros clienteCodigo="GAME" />
              </Suspense>
            }
          />
          <Route path="/game/importador" element={<Navigate to="/game/importador-ficheros?tab=stock" replace />} />
          <Route path="/game/importador-coste-entrada" element={<Navigate to="/game/importador-ficheros?tab=cdp" replace />} />
          <Route path="/game/importador-ventas-approx" element={<Navigate to="/game/importador-ficheros?tab=ventas" replace />} />
          <Route
            path="/game/importaciones"
            element={
              <Suspense fallback={<div className="p-4 text-sm text-slate-500">Cargando…</div>}>
                <Importaciones clienteCodigo="GAME" />
              </Suspense>
            }
          />
          <Route path="/game/calculadora" element={<GameCalculadora />} />
          <Route path="/game/cotizaciones" element={<GameCotizaciones />} />
          <Route path="/game/cotizaciones/:id" element={<CotizacionDetalle />} />
          <Route path="/game/parametros" element={<GameParametros />} />
          <Route path="/game/bolsa" element={<GameBolsaProvisionamiento />} />
          <Route path="/game/reposiciones" element={<ReposicionesList clienteCodigo="GAME" />} />
          <Route path="/game/reposiciones/nueva" element={<ReposicionNueva clienteCodigo="GAME" />} />
          <Route path="/game/reposiciones/:id" element={<ReposicionDetalle />} />
          <Route path="/game/pedidos" element={<GamePedidos />} />
          <Route path="/game/pedidos/:id" element={<PedidoClienteDetalle />} />

          {/* ───────── TICNOVA ───────── */}
          <Route path="/ticnova" element={<Inicio />} />
          <Route path="/ticnova/analisis" element={<AnalisisCliente />} />
          <Route path="/ticnova/productos" element={<TicnovaProductos />} />
          <Route path="/ticnova/productos/nuevo" element={<ProductoNuevo clienteCodigo="TICNOVA" />} />
          <Route path="/ticnova/productos/:id" element={<ProductoFicha />} />
          <Route path="/ticnova/productos/:id/clasica" element={<ProductoDetalle />} />
          <Route path="/ticnova/nuevos-productos" element={<TicnovaNuevosProductos />} />
          <Route path="/ticnova/nuevos-productos/:id" element={<NuevoProductoDetalle />} />
          {/* Importador de ficheros de TICNOVA (CEO, 27/10/2026): misma pantalla que GAME
              con los cuadros que le aplican; las rutas antiguas abren su pestaña. */}
          <Route
            path="/ticnova/importador-ficheros"
            element={
              <Suspense fallback={<div className="p-4 text-sm text-slate-500">Cargando importador…</div>}>
                <ImportadorFicheros clienteCodigo="TICNOVA" />
              </Suspense>
            }
          />
          <Route path="/ticnova/importador" element={<Navigate to="/ticnova/importador-ficheros?tab=stock" replace />} />
          <Route
            path="/ticnova/importaciones"
            element={
              <Suspense fallback={<div className="p-4 text-sm text-slate-500">Cargando…</div>}>
                <Importaciones clienteCodigo="TICNOVA" />
              </Suspense>
            }
          />
          <Route path="/ticnova/importador-ventas-approx" element={<Navigate to="/ticnova/importador-ficheros?tab=ventas" replace />} />
          <Route path="/ticnova/reposiciones" element={<ReposicionesList clienteCodigo="TICNOVA" />} />
          <Route path="/ticnova/reposiciones/nueva" element={<ReposicionNueva clienteCodigo="TICNOVA" />} />
          <Route path="/ticnova/reposiciones/:id" element={<ReposicionDetalle />} />
          <Route path="/ticnova/calculadora" element={<TicnovaCalculadora />} />
          <Route path="/ticnova/cotizaciones" element={<TicnovaCotizaciones />} />
          <Route path="/ticnova/icecat" element={<TicnovaIcecat />} />
          <Route path="/ticnova/cotizaciones/:id" element={<CotizacionDetalle />} />
          <Route path="/ticnova/parametros" element={<TicnovaParametros />} />
          <Route path="/ticnova/pedidos" element={<TicnovaPedidos />} />
          <Route path="/ticnova/pedidos/:id" element={<PedidoClienteDetalle />} />

          {/* ───────── Administración ───────── */}
          <Route path="/admin/clientes" element={<ClientesAdmin />} />
          <Route path="/admin/marcas" element={<MarcasAdmin />} />
          <Route path="/admin/categorias" element={<CategoriasAdmin />} />
          <Route path="/admin/subcategorias" element={<SubcategoriasAdmin />} />
          <Route path="/admin/detalles" element={<DetallesAdmin />} />
          <Route path="/admin/producto-estados" element={<ProductoEstadosAdmin />} />
          <Route path="/admin/pedido-estados" element={<PedidoEstadosAdmin />} />
          <Route path="/admin/parametros" element={<ParametrosAdmin />} />
          <Route path="/admin/envios" element={<EnviosAdmin />} />
          <Route path="/admin/condiciones-comerciales" element={<CondicionesComercialesAdmin />} />
          <Route path="/admin/game-calendario-fiscal" element={<GameCalendarioFiscalAdmin />} />
          <Route path="/admin/ticnova-familias-map" element={<TicnovaFamiliasMapAdmin />} />
          <Route path="/admin/gamas" element={<GamasAdmin />} />
          <Route path="/admin/email-templates" element={<EmailTemplatesAdmin />} />
          <Route path="/admin/producto-codigos-alias" element={<ProductoCodigosAliasAdmin />} />
          <Route path="/admin/proveedor-alias" element={<ProveedorAliasAdmin />} />

          {/* Importador CSV legacy: redirige al importador semanal de GAME */}
          <Route path="/stock" element={<Navigate to="/game/importador" replace />} />
          <Route path="/stock-legacy" element={<StockImport />} />

          {/* Redirects legacy a la nueva estructura GAME */}
          <Route path="/productos" element={<Navigate to="/game/productos" replace />} />
          <Route path="/productos/nuevo" element={<Navigate to="/game/productos/nuevo" replace />} />
          <Route path="/productos/:id" element={<RedirectWithId to={(id) => `/game/productos/${id}`} />} />
          <Route path="/nuevos-productos" element={<Navigate to="/game/nuevos-productos" replace />} />
          <Route path="/nuevos-productos/:id" element={<RedirectWithId to={(id) => `/game/nuevos-productos/${id}`} />} />
          <Route path="/pedidos" element={<Navigate to="/game/pedidos" replace />} />
          <Route path="/calculadora" element={<Navigate to="/game/calculadora" replace />} />
          <Route path="/import/game" element={<Navigate to="/game/importador" replace />} />
          <Route path="/import/game-productos" element={<Navigate to="/game/importador" replace />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </TituloRegistroProvider>
      </ClienteActivoProvider>
    </AuthProvider>
  );
}
